import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import { InputField, Button } from '@components/common'
import { Claim } from '@dataTypes/claimFeature.types'
import { isClaimApproved } from '@utils/claimUtils'
import moment from 'moment'

type ClaimSourceProps = {
  selectedClaim: Claim
  appointment: {
    data: any[]
    selected: any
    handleSelect: (appointment: any) => void
  }
  treatmentPlan: {
    data: any[]
    selected: any
    handleSelect: (treatmentPlan: any) => void
  }
  handleClaimSourceChange: (claimSource: 0 | 1) => void
}

const ClaimSource: React.FC<ClaimSourceProps> = ({
  selectedClaim,
  appointment,
  treatmentPlan,
  handleClaimSourceChange,
}) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const isApproved = isClaimApproved(selectedClaim)
  const [appointmentFieldValue, setAppointmentFieldValue] = useState('')
  const [treatmentPlanFieldValue, setTreatmentPlanFieldValue] = useState('')
  const [isAppointmentFieldFocused, setIsAppointmentFieldFocused] =
    useState(false)
  const [isTreatmentPlanFieldFocused, setIsTreatmentPlanFieldFocused] =
    useState(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const claimType = selectedClaim?.claimType?.toLowerCase()

  const appointmentNameHandleChange = (value: string): void => {
    setAppointmentFieldValue(value)
  }

  const treatmentPlanNameHandleChange = (value: string): void => {
    setTreatmentPlanFieldValue(value)
  }

  const filteredAppointments = useMemo(() => {
    return appointment.data.filter(
      (x) =>
        x.provider_id === selectedClaim.providerId &&
        (x.provider.first_name
          .toLowerCase()
          .includes(appointmentFieldValue.toLowerCase()) ||
          x.provider.last_name
            .toLowerCase()
            .includes(appointmentFieldValue.toLowerCase()) ||
          x.appointmenttypesubcategory.name
            .toLowerCase()
            .includes(appointmentFieldValue.toLowerCase())),
    )
  }, [appointment.data, appointmentFieldValue, selectedClaim.providerId])

  const filteredTreatmentPlans = useMemo(() => {
    return treatmentPlan.data.filter((x) =>
      x.tp_heading
        .toLowerCase()
        .includes(treatmentPlanFieldValue.toLowerCase()),
    )
  }, [treatmentPlanFieldValue])

  useEffect(() => {
    if (
      Object.keys(appointment.selected || {}).length > 0 &&
      isAppointmentFieldFocused === false
    ) {
      const container = document.querySelector(
        '.appointment-field .input-wrapper',
      )

      if (container) {
        const span = document.createElement('span')
        span.className = 'appointment-date-element'
        span.textContent = moment(appointment.selected.appt_date).format(
          'MM/DD/YYYY',
        )
        span.style.position = 'absolute'
        span.style.right = '14px'
        span.style.fontSize = '14px'

        container.appendChild(span)

        if (appointmentFieldValue === '') {
          setAppointmentFieldValue(
            `${appointment.selected.appointmenttypesubcategory.name} Dr. ${appointment.selected.provider.first_name} ${appointment.selected.provider.last_name}`,
          )
        }
      }
    } else {
      document
        .querySelectorAll('.appointment-date-element')
        .forEach((el) => el.remove())
    }
  }, [appointment.selected, isAppointmentFieldFocused])

  useEffect(() => {
    if (
      Object.keys(treatmentPlan.selected || {}).length > 0 &&
      isTreatmentPlanFieldFocused === false
    ) {
      const container = document.querySelector(
        '.treatment-plan-field .input-wrapper',
      )

      if (container) {
        const span = document.createElement('span')
        span.className = 'treatment-plan-date-element'
        span.textContent = moment(
          treatmentPlan.selected.date_time_created_at,
        ).format('MM/DD/YYYY')
        span.style.position = 'absolute'
        span.style.right = '14px'
        span.style.fontSize = '14px'

        container.appendChild(span)

        if (treatmentPlanFieldValue === '') {
          setTreatmentPlanFieldValue(treatmentPlan.selected.tp_heading)
        }
      }
    } else {
      document
        .querySelectorAll('.treatment-plan-date-element')
        .forEach((el) => el.remove())
    }
  }, [treatmentPlan.selected, isTreatmentPlanFieldFocused])

  return (
    <>
      <Row className="mt-2">
        <Col md={12}>
          <div className="claim-attachment-label pt-1">
            {claimType === 'pre-authorization'
              ? 'Source of claim'
              : 'Select appointment'}
          </div>
        </Col>
      </Row>

      {/* Show filter buttons only if Pre-Authorization */}
      {claimType === 'pre-authorization' && (
        <Row className="mt-1 mb-2 gx-2 claim-source-toggle">
          <Col md={6}>
            <Button
              size="lg"
              className={`w-100 ${selectedClaim.claimSource === 0 ? 'button-primary' : 'button-secondary'}`}
              onClick={() => {
                if (selectedClaim.claimSource === 1) {
                  handleClaimSourceChange(0)
                  setTreatmentPlanFieldValue('')
                }
              }}
              disabled={isApproved}
            >
              Appointment
            </Button>
          </Col>
          <Col md={6}>
            <Button
              size="lg"
              className={`w-100 ${selectedClaim.claimSource === 1 ? 'button-primary' : 'button-secondary'}`}
              onClick={() => {
                if (selectedClaim.claimSource === 0) {
                  handleClaimSourceChange(1)
                  setAppointmentFieldValue('')
                }
              }}
              disabled={isApproved}
            >
              Treatment plan
            </Button>
          </Col>
        </Row>
      )}

      {selectedClaim.claimSource === 0 && (
        <Row className="gx-2 claim-source-button">
          <Col md={12}>
            <Form.Group controlId="sourceSelection">
              <InputField
                label=""
                value={appointmentFieldValue || ''}
                placeholder="Appointment"
                leftIcon={<i className="ti ti-search"></i>}
                onChange={(e) => appointmentNameHandleChange(e.target.value)}
                onFocus={() => setIsAppointmentFieldFocused(true)}
                onBlur={() =>
                  setTimeout(() => setIsAppointmentFieldFocused(false), 300)
                }
                className={'appointment-field'}
                disabled={!!appointment.selected?.appointment_id}
              />
              {filteredAppointments.length > 0 && isAppointmentFieldFocused && (
                <Col md={6}>
                  <ul className="appointment-list">
                    {filteredAppointments
                      .slice()
                      .sort(
                        (a, b) =>
                          new Date(a.appt_date).getTime() -
                          new Date(b.appt_date).getTime(),
                      )
                      .map((x) => (
                        <li
                          key={x.appointment_id}
                          onClick={() => {
                            appointment.handleSelect(x)
                            setAppointmentFieldValue(
                              `${x.appointmenttypesubcategory.name} Dr. ${x.provider.first_name} ${x.provider.last_name}`,
                            )
                          }}
                          className="appointment-dropdown-item"
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <span style={{ fontWeight: '500' }}>
                              {x.appointmenttypesubcategory.name}
                            </span>
                            <span style={{ margin: '0 8px' }}>
                              Dr. {x.provider.first_name} {x.provider.last_name}
                            </span>
                            <span style={{ marginLeft: 'auto' }}>
                              {moment(x.appt_date).format('MM/DD/YYYY')}
                            </span>
                          </div>
                        </li>
                      ))}
                  </ul>
                </Col>
              )}
              {appointment.selected && appointment.selected.appointment_id && (
                <div
                  key={appointment.selected.appointment_id}
                  className="selected-item"
                >
                  {appointmentFieldValue}
                  <button
                    type="button"
                    className="unstyled-button selected-item-remove ti ti-x"
                    aria-label="Remove appointment"
                    onClick={() => {
                      appointment.handleSelect(null)
                      setAppointmentFieldValue('')
                    }}
                  />
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      )}
      {selectedClaim.claimSource === 1 && (
        <Row className="gx-2 claim-source-button">
          <Col md={12}>
            <Form.Group controlId="sourceSelection">
              <InputField
                label=""
                value={treatmentPlanFieldValue || ''}
                placeholder="Treatment plan"
                leftIcon={<i className="ti ti-search"></i>}
                onChange={(e) => treatmentPlanNameHandleChange(e.target.value)}
                onFocus={() => setIsTreatmentPlanFieldFocused(true)}
                onBlur={() =>
                  setTimeout(() => setIsTreatmentPlanFieldFocused(false), 300)
                }
                className={'treatment-plan-field'}
                disabled={!!treatmentPlan.selected?.tp_id}
              />
              {filteredTreatmentPlans.length > 0 &&
                isTreatmentPlanFieldFocused && (
                  <Col md={6}>
                    <ul className="treatment-plan-list">
                      {filteredTreatmentPlans
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(a.date_time_created_at).getTime() -
                            new Date(b.date_time_created_at).getTime(),
                        )
                        .map((x) => (
                          <li
                            key={x.tp_id}
                            onClick={() => {
                              treatmentPlan.handleSelect(x)
                              setTreatmentPlanFieldValue(x.tp_heading)
                            }}
                            className="treatment-plan-dropdown-item"
                          >
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <span style={{ fontWeight: '500' }}>
                                {x.tp_heading}
                              </span>
                              <span style={{ marginLeft: 'auto' }}>
                                {moment(x.date_time_created_at).format(
                                  'MM/DD/YYYY',
                                )}
                              </span>
                            </div>
                          </li>
                        ))}
                    </ul>
                  </Col>
                )}
              {treatmentPlan.selected && treatmentPlan.selected.tp_id && (
                <div
                  key={treatmentPlan.selected.tp_id}
                  className="selected-item"
                >
                  {treatmentPlanFieldValue}
                  <button
                    type="button"
                    className="unstyled-button selected-item-remove ti ti-x"
                    aria-label="Remove treatment plan"
                    onClick={() => {
                      treatmentPlan.handleSelect(null)
                      setTreatmentPlanFieldValue('')
                    }}
                  />
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      )}
    </>
  )
}

export default ClaimSource
